/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/prototype 层；
 * 2. 负责定义“特征对象本身”的统一抽象基类；
 * 3. Prototype 是设计树、编辑会话、结果体对象之间共享的领域对象入口。
 *
 * 文件职责：
 * 1. 统一管理 featureId、definition、bindings、sourceSketchId；
 * 2. 让 ResultObject / PreviewObject 能直接关联回特征对象，而不是依赖零散 metadata；
 * 3. 为后续 Revolve、Sweep、Loft 等特征提供一致的可扩展骨架。
 */

import type { FeatureReferenceBinding } from '../binding/FeatureReferenceBinding';
import type { FeatureDefinition } from '../definition/FeatureDefinition';

/**
 * 归一化来源草图 ID。
 *
 * 说明：
 * 1. 空字符串不是有效草图 ID，不能进入特征原型的公共语义；
 * 2. 回退时间线、依赖图、设计树和 Undo/Redo replay 都依赖同一份 sourceSketchId；
 * 3. 在原型入口统一清洗，可以避免各调用点各自写兜底逻辑。
 *
 * @param sketchId 候选草图 ID。
 * @returns 有效草图 ID；无效时返回 null。
 */
function normalizeSourceSketchId(sketchId: string | null | undefined): string | null {
    if (typeof sketchId !== 'string') {
        return null;
    }
    const trimmedSketchId = sketchId.trim();
    return trimmedSketchId.length > 0 ? trimmedSketchId : null;
}

/**
 * 特征原型抽象基类。
 */
export abstract class FeaturePrototype<TDefinition extends FeatureDefinition = FeatureDefinition> {
    /** 特征唯一标识。 */
    private readonly featureId: string;
    /** 特征定义对象。 */
    private readonly definition: TDefinition;
    /** 特征输入绑定集合。 */
    private readonly bindings: FeatureReferenceBinding[];
    /** 来源草图 ID。 */
    private readonly sourceSketchId: string | null;

    /**
     * @param input 输入参数。
     */
    protected constructor(input: {
        featureId: string;
        definition: TDefinition;
        bindings: FeatureReferenceBinding[];
        sourceSketchId?: string | null;
    }) {
        this.featureId = input.featureId;
        this.definition = input.definition;
        this.bindings = input.bindings.map((binding) => binding.clone());
        this.sourceSketchId = normalizeSourceSketchId(input.sourceSketchId);
    }

    /**
     * 获取特征 ID。
     *
     * @returns 特征 ID。
     */
    public getFeatureId(): string {
        return this.featureId;
    }

    /**
     * 获取特征定义对象。
     *
     * @returns 特征定义对象。
     */
    public getDefinition(): TDefinition {
        return this.definition;
    }

    /**
     * 获取特征语义类型。
     *
     * @returns 特征语义类型。
     */
    public getFeatureKind(): string {
        return this.definition.getFeatureKind();
    }

    /**
     * 获取输入绑定集合。
     *
     * @returns 绑定集合副本。
     */
    public getBindings(): FeatureReferenceBinding[] {
        return this.bindings.map((binding) => binding.clone());
    }

    /**
     * 获取来源草图 ID。
     *
     * @returns 来源草图 ID；无来源时返回 null。
     */
    public getSourceSketchId(): string | null {
        return this.sourceSketchId;
    }
}
